import React, { useState, useEffect } from 'react';
import { X, Truck, Container, Car, Hash, FileText, Link2, Camera, Upload, Phone, User } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useAuth from '../../hooks/useAuth';
import SearchableSelect from '../adminpanel/SearchableSelect';

const TRAILER_TYPES = new Set(['semi_trailer']);
const RUS_LETTERS = 'ABEKMHOPCTYX';
const ARM_PATTERNS = {
  vehicle: [/^\d{2}[A-Z]{2}\d{3}$/, /^\d{3}[A-Z]{2}\d{2}$/],
  trailer: [/^\d{2}[A-Z]\d{3}$/, /^\d{3}[A-Z]\d{2}$/],
};
const RUS_PATTERNS = {
  vehicle: [new RegExp(`^[${RUS_LETTERS}]\\d{3}[${RUS_LETTERS}]{2}\\d{2,3}$`)],
  trailer: [new RegExp(`^[${RUS_LETTERS}]{2}\\d{6,7}$`)],
};

function validatePlateFormat(plate, vehicleType, companyCountry) {
  if (!plate) return null;
  const up = plate.toUpperCase();
  // Universal check (applies even without a company_country): a plate may only
  // contain Latin letters and digits — blocks Cyrillic / symbols / spaces.
  if (!/^[A-Z0-9]+$/.test(up)) {
    return 'Plate may contain only Latin letters and digits.';
  }
  const isTrailer = TRAILER_TYPES.has(vehicleType);
  const key = isTrailer ? 'trailer' : 'vehicle';
  // Known country → its format. Unknown country → must match ANY known format.
  let patterns, isArm;
  if (companyCountry === 'arm') { patterns = ARM_PATTERNS[key]; isArm = true; }
  else if (companyCountry === 'rus') { patterns = RUS_PATTERNS[key]; isArm = false; }
  else { patterns = [...ARM_PATTERNS[key], ...RUS_PATTERNS[key]]; isArm = null; }
  if (!patterns.some(p => p.test(up))) {
    if (isArm === true) {
      return isTrailer ? 'Invalid plate format. Expected: 00X000 or 000X00'
                       : 'Invalid plate format. Expected: 00XX000 or 000XX00';
    }
    if (isArm === false) {
      return isTrailer ? 'Invalid plate format. Expected: XX000000(0) (e.g. AB123477)'
                       : 'Invalid plate format. Expected: X000XX00(0) (e.g. A123BC77)';
    }
    return isTrailer
      ? 'Invalid plate format. Expected: 00X000 / 000X00 or XX000000(0)'
      : 'Invalid plate format. Expected: 00XX000 / 000XX00 or X000XX00(0)';
  }
  return null;
}

const AddVehicleForm = ({
  isOpen, onClose, onSubmit, initialData, allVehicles = [],
  // Admin mode: when `users` is provided, an owner selector is shown and the
  // plate is validated against the owner's `companyCountry`.
  users = null, ownerId = '', onOwnerChange = null, companyCountry = null,
  // All active vehicles for GLOBAL plate uniqueness (mirrors the DB constraint).
  // Falls back to allVehicles when not provided.
  uniquenessVehicles = null,
}) => {
  const isAdminMode = Array.isArray(users);
  const [formData, setFormData] = useState({
    type: initialData?.type || 'tractor',
    plate_number: initialData?.plate_number || '',
    vin: initialData?.vin || '',
    passport_number: initialData?.passport_number || '',
    vehicle_year: initialData?.vehicle_year || '',
    make: initialData?.make || '',
    model: initialData?.model || '',
    hitch_to: initialData?.hitch_to || '',
    net_weight: initialData?.net_weight || '',
    photo_front: initialData?.photo_front || '',
    photo_back: initialData?.photo_back || '',
    driver_passport_photo: initialData?.driver_passport_photo || '',
    driver_name: initialData?.driver_name || '',
	driver_phonenumber: initialData?.driver_phonenumber || '',
    driver_passport_number: initialData?.driver_passport_number || '',
  });

  const [photoFrontFile, setPhotoFrontFile] = useState(null);
  const [plateError, setPlateError] = useState('');
  const [vinError, setVinError] = useState('');
  const { language, setLanguage, t } = useLanguage();
  const { user } = useAuth();
  // Country used for plate-format validation: owner's in admin mode, else current user's.
  const effectiveCountry = companyCountry != null ? companyCountry : (user?.company_country || '');

  // Plate uniqueness is GLOBAL (across all active vehicles) — matches the DB
  // constraint & Django admin. VIN uniqueness is within the owner's vehicles.
  const plateUniqList = Array.isArray(uniquenessVehicles) ? uniquenessVehicles : allVehicles;
  const plateDuplicate = (plate) =>
    !!plate && plateUniqList.some(v => (v.plate_number || '').toUpperCase() === plate.toUpperCase() && v.id !== initialData?.id);
  const vinDuplicate = (vin) =>
    !!vin && allVehicles.some(v => (v.vin || '').toUpperCase() === vin.toUpperCase() && v.id !== initialData?.id);
  const [photoBackFile, setPhotoBackFile] = useState(null);
  const [driverPassportFile, setDriverPassportFile] = useState(null);
  const [previewImage, setPreviewImage] = useState({ open: false, src: '', title: '' });

  useEffect(() => {
    if (initialData) {
      setFormData({
        type: initialData.type || 'tractor',
        plate_number: initialData.plate_number || '',
        vin: initialData.vin || '',
        passport_number: initialData.passport_number || '',
        vehicle_year: initialData.vehicle_year || '',
        make: initialData.make || '',
        model: initialData.model || '',
        hitch_to: initialData.hitch_to || '',
        net_weight: initialData.net_weight || '',
        photo_front: initialData.photo_front || '',
        photo_back: initialData.photo_back || '',
        driver_passport_photo: initialData.driver_passport_photo || '',
        driver_name: initialData.driver_name || '',
		driver_phonenumber: initialData.driver_phonenumber || '',
        driver_passport_number: initialData.driver_passport_number || '',
      });
    } else {
      setFormData({
        type: 'tractor',
        plate_number: '',
        vin: '',
        passport_number: '',
        vehicle_year: '',
        make: '',
        model: '',
        hitch_to: '',
        net_weight: '',
        photo_front: '',
        photo_back: '',
        driver_passport_photo: '',
        driver_name: '',
		driver_phonenumber: '',
        driver_passport_number: '',
      });
    }
    setPhotoFrontFile(null);
    setPhotoBackFile(null);
    setDriverPassportFile(null);
    setPlateError('');
    setVinError('');
  }, [initialData, isOpen]);

  // Re-validate whenever the plate/type/owner-country or vehicle lists change
  // (e.g. the admin selects the owner AFTER typing a plate).
  useEffect(() => {
    const fmt = validatePlateFormat(formData.plate_number, formData.type, effectiveCountry);
    setPlateError(fmt || (plateDuplicate(formData.plate_number) ? 'A vehicle with this plate number already exists.' : ''));
    setVinError(vinDuplicate(formData.vin) ? 'This owner already has a vehicle with this VIN.' : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.plate_number, formData.vin, formData.type, effectiveCountry, ownerId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'type') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        hitch_to: '',
      }));
      // Re-validate plate with new type
      const err = validatePlateFormat(formData.plate_number, value, effectiveCountry);
      setPlateError(err || '');
    } else if (name === 'plate_number') {
      const up = value.toUpperCase();
      setFormData(prev => ({ ...prev, [name]: up }));
      const err = validatePlateFormat(up, formData.type, effectiveCountry);
      setPlateError(err || (plateDuplicate(up) ? 'A vehicle with this plate number already exists.' : ''));
    } else if (name === 'vin') {
      const up = value.toUpperCase();
      setFormData(prev => ({ ...prev, [name]: up }));
      setVinError(vinDuplicate(up) ? 'This owner already has a vehicle with this VIN.' : '');
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      if (fieldName === 'photo_front') {
        setPhotoFrontFile(file);
        const previewUrl = URL.createObjectURL(file);
        setFormData(prev => ({ ...prev, photo_front: previewUrl }));
      } else if (fieldName === 'photo_back') {
        setPhotoBackFile(file);
        const previewUrl = URL.createObjectURL(file);
        setFormData(prev => ({ ...prev, photo_back: previewUrl }));
      } else if (fieldName === 'driver_passport_photo') {
        setDriverPassportFile(file);
        const previewUrl = URL.createObjectURL(file);
        setFormData(prev => ({ ...prev, driver_passport_photo: previewUrl }));
      }
    }
  };

  const handleImageClick = (src, title) => {
    setPreviewImage({ open: true, src, title });
  };

  const closePreview = () => {
    setPreviewImage({ open: false, src: '', title: '' });
  };

	const handleSubmit = async (e) => {
	  e.preventDefault();

	  // Guards without popups — the Save button is disabled while any of these
	  // fail, and the reason is shown inline under the field.
	  if (!formData.plate_number.trim()) {
		setPlateError('Plate number is required.');
		return;
	  }
	  if (isAdminMode && !ownerId) return;

	  const err = validatePlateFormat(formData.plate_number, formData.type, effectiveCountry);
	  if (err) { setPlateError(err); return; }
	  if (plateDuplicate(formData.plate_number)) {
		setPlateError('A vehicle with this plate number already exists.');
		return;
	  }
	  if (vinDuplicate(formData.vin)) {
		setVinError('This owner already has a vehicle with this VIN.');
		return;
	  }

	  // Normalize hitch_to value - convert empty string to null
	  const hitchTo = formData.hitch_to && formData.hitch_to.trim() !== '' ? formData.hitch_to.trim() : null;
	  
	  // Calculate max_load if hitch_to is selected
	  let maxLoad = 0;
	  if (hitchTo) {
		const hitchedVehicle = allVehicles.find(v => v.plate_number === hitchTo);
		if (hitchedVehicle) {
		  const currentNetWeight = formData.net_weight ? parseInt(formData.net_weight) : 0;
		  const hitchedNetWeight = hitchedVehicle.net_weight || 0;
		  maxLoad = 40000 - currentNetWeight - hitchedNetWeight;
		}
	  }
	  
	  // Check which photos were removed
	  const delete_photo_front = initialData?.photo_front && !formData.photo_front;
	  const delete_photo_back = initialData?.photo_back && !formData.photo_back;
	  const delete_driver_passport_photo = initialData?.driver_passport_photo && !formData.driver_passport_photo;
	  
	  const submitData = {
		type: formData.type,
		plate_number: formData.plate_number,
		vin: formData.vin,
		passport_number: formData.passport_number,
		vehicle_year: formData.vehicle_year,
		make: formData.make,
		model: formData.model,
        driver_name: formData.driver_name,
		driver_phonenumber: formData.driver_phonenumber,
        driver_passport_number: formData.driver_passport_number,		
		net_weight: formData.net_weight ? parseInt(formData.net_weight) : 0,
		max_load: maxLoad,
		hitch_to: hitchTo,
		photo_front_file: photoFrontFile || null,
		photo_back_file: photoBackFile || null,
		driver_passport_file: driverPassportFile || null,
		// Add deletion flags for backend
		delete_photo_front: delete_photo_front, 
		delete_photo_back: delete_photo_back,
		delete_driver_passport_photo: delete_driver_passport_photo
	  };

	  // Admin mode: tie the vehicle to the selected owner.
	  if (isAdminMode && ownerId) {
		submitData.created_by = ownerId;
	  }

	  // Submit; surface any backend validation errors inline (no popups).
	  try {
		await onSubmit(submitData);
	  } catch (err) {
		let parsed = null;
		try { parsed = JSON.parse(err.message); } catch (_) {}
		if (parsed && typeof parsed === 'object') {
		  const pick = (v) => Array.isArray(v) ? v[0] : String(v);
		  if (parsed.plate_number) setPlateError(pick(parsed.plate_number));
		  if (parsed.vin) setVinError(pick(parsed.vin));
		  const others = Object.keys(parsed).filter(k => k !== 'plate_number' && k !== 'vin');
		  if (others.length && !parsed.plate_number && !parsed.vin) {
			setPlateError(others.map(k => `${k}: ${pick(parsed[k])}`).join('; '));
		  }
		} else {
		  setPlateError(err.message || 'Failed to save vehicle.');
		}
	  }
	};

  const removePhoto = (fieldName) => {
    if (fieldName === 'photo_front') {
      setPhotoFrontFile(null);
      setFormData(prev => ({ ...prev, photo_front: '' }));
    } else if (fieldName === 'photo_back') {
      setPhotoBackFile(null);
      setFormData(prev => ({ ...prev, photo_back: '' }));
    } else if (fieldName === 'driver_passport_photo') {
      setDriverPassportFile(null);
      setFormData(prev => ({ ...prev, driver_passport_photo: '' }));
    }
  };

  // Get available vehicles for hitching based on current vehicle type
  const getAvailableHitchVehicles = () => {
    const currentVehicleId = initialData?.id;
    
    switch (formData.type) {
      case 'tractor':
        // Tractors can hitch to semi-trailers
        return allVehicles.filter(v => 
          v.type === 'semi_trailer' && 
          v.id !== currentVehicleId
        );
      
      case 'semi_trailer':
        // Semi-trailers can be hitched by tractors or trucks
        return [];
      
      case 'truck':
        // Trucks can hitch to semi-trailers
        return allVehicles.filter(v => 
          v.type === 'semi_trailer' && 
          v.id !== currentVehicleId
        );
      
      case 'other':
        // Others don't show hitch option
        return [];
      
      default:
        return [];
    }
  };

  const availableHitchVehicles = getAvailableHitchVehicles();
  const showHitchField = formData.type !== 'other' && formData.type !== 'semi_trailer';

  // In admin mode, lock all fields until an owner is selected (the owner's
  // country drives plate validation, and vehicles are tied to that owner).
  const fieldsLocked = isAdminMode && !ownerId;

  // Disable Save while the form has a validation problem (no popups).
  const formInvalid =
    fieldsLocked ||
    !!plateError || !!vinError ||
    !formData.plate_number.trim();

  if (!isOpen) return null;

  const vehicleTypes = [
    { value: 'tractor', label: t('tractor'), icon: Truck },
    { value: 'semi_trailer', label: t('trailer'), icon: Container },
    { value: 'truck', label: t('truck'), icon: Truck },
    { value: 'other', label: t('other'), icon: Car }
  ];

  return (
    <>
      {/* Image Preview Modal */}
      {previewImage.open && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4">
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={closePreview}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 text-2xl z-10"
            >
              <X className="h-8 w-8" />
            </button>
            <div className="bg-white p-2 rounded-lg">
              <h3 className="text-lg font-semibold mb-2 text-center text-black">
                {previewImage.title}
              </h3>
              <img 
                src={previewImage.src} 
                alt={previewImage.title}
                className="max-w-full max-h-[80vh] object-contain rounded"
              />
            </div>
          </div>
        </div>
      )}

      {/* Main Form Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 bg-gradient-to-r from-slate-800 to-slate-700 text-white">
            <h2 className="text-xl font-semibold">
              {initialData ? t('edit') : t('regVehicle')}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-white hover:text-gray-200 rounded-full transition duration-150"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <form onSubmit={handleSubmit}>
              {/* Owner (admin mode only) */}
              {isAdminMode && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Owner (user) *</label>
                  <SearchableSelect
                    icon={<User className="h-5 w-5 text-gray-400" />}
                    placeholder="— Select user —"
                    value={ownerId}
                    options={users.map(u => ({ value: u.id, label: u.username }))}
                    onChange={(val) => onOwnerChange && onOwnerChange(val)}
                  />
                  {fieldsLocked && (
                    <p className="mt-1 text-xs text-amber-600">Select an owner to enable the rest of the form.</p>
                  )}
                </div>
              )}

              {/* All other fields — locked until an owner is selected (admin mode) */}
              <fieldset disabled={fieldsLocked} className={fieldsLocked ? 'opacity-50' : ''}>

              {/* Vehicle Type */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t('vehicleType')} *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {vehicleTypes.map((vehicleType) => {
                    const IconComponent = vehicleType.icon;
                    return (
                      <label
                        key={vehicleType.value}
                        className={`relative flex flex-col items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.type === vehicleType.value
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          name="type"
                          value={vehicleType.value}
                          checked={formData.type === vehicleType.value}
                          onChange={handleChange}
                          className="absolute opacity-0"
                        />
                        <IconComponent className={`h-6 w-6 mb-2 ${
                          formData.type === vehicleType.value ? 'text-blue-600' : 'text-gray-600'
                        }`} />
                        <span className={`text-sm font-medium ${
                          formData.type === vehicleType.value ? 'text-blue-700' : 'text-gray-700'
                        }`}>
                          {vehicleType.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  {/* Plate Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('licensePlate')} *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Hash className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="plate_number"
                        value={formData.plate_number}
                        onChange={handleChange}
                        required
                        maxLength={20}
                        className={`block w-full pl-10 pr-3 py-2 border rounded-md focus:ring-2 ${plateError ? 'border-red-500 bg-red-50 focus:ring-red-300 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}
                        placeholder={t('licPlateExam')}
                      />
                    </div>
                    {plateError && (
                      <p className="mt-1 text-xs text-red-500">{plateError}</p>
                    )}
                  </div>

                  {/* VIN */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      VIN
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FileText className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="vin"
                        value={formData.vin}
                        onChange={handleChange}
                        maxLength={17}
                        className={`block w-full pl-10 pr-3 py-2 border rounded-md focus:ring-2 ${vinError ? 'border-red-500 bg-red-50 focus:ring-red-300 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}
                        placeholder={t('vinDesc')}
                      />
                    </div>
                    {vinError && <p className="mt-1 text-xs text-red-500">{vinError}</p>}
                  </div>

                  {/* Passport Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('vehiclePassport')}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FileText className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="passport_number"
                        value={formData.passport_number}
                        onChange={handleChange}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder={t('vehPassDesc')}
                      />
                    </div>
                  </div>

                  {/* Year */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('vehYear')}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FileText className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="vehicle_year"
                        value={formData.vehicle_year}
                        onChange={handleChange}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder={t('vehYearExam')}
                      />
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  {/* Make */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('manufacturer')}
                    </label>
                    <input
                      type="text"
                      name="make"
                      value={formData.make}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={t('makeExam')}
                    />
                  </div>

                  {/* Model */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('model')}
                    </label>
                    <input
                      type="text"
                      name="model"
                      value={formData.model}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={t('modelExam')}
                    />
                  </div>

                  {/* Hitch To - Now a dropdown */}
                  {showHitchField && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('hitchTo')}
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Link2 className="h-5 w-5 text-gray-400" />
                        </div>
                        <select
                          name="hitch_to"
                          value={formData.hitch_to}
                          onChange={handleChange}
                          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        >
                          <option value="">-- {t('trailerSelect')} --</option>
                          {availableHitchVehicles.map(vehicle => {
                            // Check if vehicle is already hitched to something else
                            // For tractors/trucks: check if they have hitch_to value
                            // For semi-trailers: check if any other vehicle is hitched to them
                            let isAlreadyHitched = false;
                            
                            if (vehicle.type === 'tractor' || vehicle.type === 'truck') {
                              // Tractor/truck is already hitched if it has hitch_to value (and it's not the current vehicle)
                              isAlreadyHitched = vehicle.hitch_to && vehicle.hitch_to !== '' && vehicle.hitch_to !== formData.plate_number;
                            } else if (vehicle.type === 'semi_trailer') {
                              // Semi-trailer is already hitched if another vehicle is hitched to it
                              isAlreadyHitched = allVehicles.some(v => 
                                v.id !== initialData?.id && 
                                v.hitch_to === vehicle.plate_number
                              );
                            }
                            
                            return (
                              <option 
                                key={vehicle.id} 
                                value={vehicle.plate_number}
                                disabled={isAlreadyHitched}
                                style={isAlreadyHitched ? { color: '#9CA3AF' } : {}}
                              >
                                {vehicle.plate_number}
                                {vehicle.make && ` (${vehicle.make} ${vehicle.model || ''})`}
									{' '} 
                                {isAlreadyHitched ?  t('alreadyHitched') : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      {availableHitchVehicles.length === 0 && (
                        <p className="mt-1 text-xs text-gray-500">
                          No compatible vehicles available for hitching
                        </p>
                      )}
                    </div>
                  )}

                  {/* Net Weight */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('netWeight')}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <p className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="number"
                        name="net_weight"
                        value={formData.net_weight}
                        onChange={handleChange}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder={t('netWeightExam')}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Driver Information - Only show for non-semi-trailer vehicles */}
              {formData.type !== 'semi_trailer' && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">{t('infoDriver')}</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Driver Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('firstName')} {t('lastName')}
                      </label>
					<div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                      <input
                        type="text"
                        name="driver_name"
                        value={formData.driver_name}
                        onChange={handleChange}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder={t('driverInfo')}
                      />
					 </div>
                    </div>

                    {/* Driver Passport Number */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('driverPass')}
                      </label>
                      <input
                        type="text"
                        name="driver_passport_number"
                        value={formData.driver_passport_number}
                        onChange={handleChange}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="AL112233"
                      />
                    </div>
					{/* Driver Phone Number */}
					  <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('phone')}
                      </label>
					  <div className="relative">
					  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                      <input
                        type="text"
                        name="driver_phonenumber"
                        value={formData.driver_phonenumber}
                        onChange={handleChange}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
                        placeholder="077029696"
                      />
					  </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Photo Upload Section */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{t('vehPassPhoto')}</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Front Photo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('frontPhoto')}
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                      {formData.photo_front ? (
                        <div className="relative">
                          <img 
                            src={formData.photo_front} 
                            alt="Vehicle front" 
                            className="mx-auto h-32 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => handleImageClick(formData.photo_front, 'Vehicle Front Photo')}
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto('photo_front')}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange(e, 'photo_front')}
                            className="hidden"
                          />
                          <Camera className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-600">Click to upload front photo</span>
                          <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 10MB</p>
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Back Photo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('backPhoto')}
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                      {formData.photo_back ? (
                        <div className="relative">
                          <img 
                            src={formData.photo_back} 
                            alt="Vehicle back" 
                            className="mx-auto h-32 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => handleImageClick(formData.photo_back, 'Vehicle Back Photo')}
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto('photo_back')}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange(e, 'photo_back')}
                            className="hidden"
                          />
                          <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-600">Click to upload back photo</span>
                          <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 10MB</p>
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Driver Passport Photo - Only show for non-semi-trailer vehicles */}
              {formData.type !== 'semi_trailer' && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {t('driverPassPhoto')}
                  </h3>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                    {formData.driver_passport_photo ? (
                      <div className="relative">
                        <img 
                          src={formData.driver_passport_photo} 
                          alt="Driver passport" 
                          className="mx-auto h-32 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => handleImageClick(formData.driver_passport_photo, 'Driver Passport Photo')}
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto('driver_passport_photo')}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, 'driver_passport_photo')}
                          className="hidden"
                        />
                        <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">Click to upload driver passport</span>
                        <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 10MB</p>
                      </label>
                    )}
                  </div>
                </div>
              )}

              </fieldset>
            </form>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              {t('cancel')}
            </button>
            
            <button
              type="button"
              onClick={handleSubmit}
              disabled={formInvalid}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {initialData ? t('update') : t('register')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddVehicleForm;